/**
 * Logger Utility - Debug logging for PF MolView
 *
 * Provides structured logging with levels and categories for debugging.
 * All logs go to browser console with optional persistence.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
}

/**
 * Logger configuration
 */
interface LoggerConfig {
  /** Minimum log level to output */
  minLevel: LogLevel;
  /** Whether to include timestamps */
  showTimestamp: boolean;
  /** Whether to store logs in memory for later retrieval */
  storeInMemory: boolean;
  /** Maximum number of logs to keep in memory */
  maxStoredLogs: number;
  /** Categories to filter (empty = all) */
  enabledCategories: string[];
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: 'debug',
  showTimestamp: true,
  storeInMemory: true,
  maxStoredLogs: 500,
  enabledCategories: [], // empty = all enabled
};

/**
 * PF MolView Logger
 */
class MolViewLogger {
  private config: LoggerConfig;
  private storedLogs: LogEntry[] = [];
  private static instance: MolViewLogger | null = null;

  private constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  static getInstance(): MolViewLogger {
    if (!MolViewLogger.instance) {
      MolViewLogger.instance = new MolViewLogger();
    }
    return MolViewLogger.instance;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.config.minLevel = level;
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel, category: string): boolean {
    // Check level
    if (LOG_LEVELS[level] < LOG_LEVELS[this.config.minLevel]) {
      return false;
    }

    // Check category filter
    if (
      this.config.enabledCategories.length > 0 &&
      !this.config.enabledCategories.includes(category)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Format log message
   */
  private formatMessage(entry: LogEntry): string {
    const parts: string[] = [];

    if (this.config.showTimestamp) {
      const time = entry.timestamp.toISOString().split('T')[1].slice(0, 12);
      parts.push(`[${time}]`);
    }

    parts.push(`[${entry.category}]`);
    parts.push(entry.message);

    return parts.join(' ');
  }

  /**
   * Store log entry in memory
   */
  private storeLog(entry: LogEntry): void {
    if (!this.config.storeInMemory) return;

    this.storedLogs.push(entry);

    // Trim old logs
    if (this.storedLogs.length > this.config.maxStoredLogs) {
      this.storedLogs = this.storedLogs.slice(-this.config.maxStoredLogs);
    }
  }

  /**
   * Core log method
   */
  private log(level: LogLevel, category: string, message: string, data?: unknown): void {
    if (!this.shouldLog(level, category)) return;

    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      category,
      message,
      data,
    };

    this.storeLog(entry);

    const formattedMessage = this.formatMessage(entry);
    const consoleMethod = level === 'debug' ? 'log' : level;

    if (data !== undefined) {
      console[consoleMethod](formattedMessage, data);
    } else {
      console[consoleMethod](formattedMessage);
    }
  }

  // =========================================================================
  // Public logging methods
  // =========================================================================

  debug(category: string, message: string, data?: unknown): void {
    this.log('debug', category, message, data);
  }

  info(category: string, message: string, data?: unknown): void {
    this.log('info', category, message, data);
  }

  warn(category: string, message: string, data?: unknown): void {
    this.log('warn', category, message, data);
  }

  error(category: string, message: string, data?: unknown): void {
    this.log('error', category, message, data);
  }

  /**
   * Log an error with stack trace
   */
  exception(category: string, message: string, error: Error): void {
    this.log('error', category, `${message}: ${error.message}`, {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
  }

  // =========================================================================
  // Log retrieval
  // =========================================================================

  /**
   * Get all stored logs
   */
  getLogs(): LogEntry[] {
    return [...this.storedLogs];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.storedLogs.filter(log => log.level === level);
  }

  /**
   * Get logs filtered by category
   */
  getLogsByCategory(category: string): LogEntry[] {
    return this.storedLogs.filter(log => log.category === category);
  }

  /**
   * Get recent errors
   */
  getErrors(): LogEntry[] {
    return this.getLogsByLevel('error');
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    this.storedLogs = [];
  }

  /**
   * Export logs as JSON string
   */
  exportLogs(): string {
    return JSON.stringify(this.storedLogs, null, 2);
  }

  /**
   * Print summary of errors to console
   */
  printErrorSummary(): void {
    const errors = this.getErrors();
    if (errors.length === 0) {
      console.log('[PF MolView] No errors recorded');
      return;
    }

    console.group(`[PF MolView] Error Summary (${errors.length} errors)`);
    errors.forEach((entry, i) => {
      console.log(`${i + 1}. [${entry.category}] ${entry.message}`);
      if (entry.data) {
        console.log('   Data:', entry.data);
      }
    });
    console.groupEnd();
  }
}

// Export singleton instance
export const logger = MolViewLogger.getInstance();

// Export categories for consistency
export const LogCategories = {
  VIEWER: 'Viewer',
  STORE: 'Store',
  FILE: 'FileLoader',
  UI: 'UI',
  GEOMETRY: 'Geometry',
  RENDER: 'Render',
  MEASURE: 'Measure',
  SELECT: 'Select',
} as const;

// Expose logger globally for console debugging
if (typeof window !== 'undefined') {
  (window as any).molviewLogger = logger;
  (window as any).molviewGetErrors = () => logger.printErrorSummary();
}
